import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { WorkspaceTemplateConfigurationService } from '@app/feature/header/workspace-controls/workspace-services/workspace-template-configuration.service'
import { InputCellContainerComponent } from '@app/feature/input-cells/input-cell-container/input-cell-container.component'
import { ManageFieldsService } from '@app/feature/manage-fields/manage-fields.service'
import {
    CellContainerComponent,
    NEW_CONTAINER_CELLS,
} from '@app/shared/cell-types/cell-container.component'
import { ShowIfOperationCodeMatchExpectedOperationDirective } from '@app/shared/show-if-operation-code-match-expected-operation.directive'
import { TbButtonWithOptionsComponent } from '@components-library/tb-button-with-options/tb-button-with-options.component'
import { TbButtonComponent } from '@components-library/tb-button/tb-button.component'
import { CommonDialogResultStatus } from '@components-library/tb-confirmation-popup/tb-confirmation-dialog.component'
import { TbInputComponent } from '@components-library/tb-input/tb-input.component'

import { TbMenuListItemComponent } from '@components-library/tb-menu-list-item/tb-menu-list-item.component'
import { TbMenuListComponent } from '@components-library/tb-menu-list/tb-menu-list.component'
import { ModalContainerComponent } from '@components-library/tb-modal-manager/modal-container-component/modal-container.component'
import { ModalLayoutButtonsComponent } from '@components-library/tb-modal-manager/modal-layout-buttons/modal-layout-buttons.component'
import { ModalLayoutComponent } from '@components-library/tb-modal-manager/modal-layout/modal-layout.component'

import { TbOptionComponent } from '@components-library/tb-select/components/tb-option/tb-option.component'
import { TbSelectComponent } from '@components-library/tb-select/components/tb-select/tb-select.component'
import { TbToggleComponent } from '@components-library/tb-toggle/tb-toggle.component'
import { whitespaceValidationPattern } from '@core/global-util'
import {
    AddFieldData,
    Field,
    FieldEntities,
    FieldType,
    FieldTypes,
    isWorkspaceGlobal,
    UpdateResponseModel,
    Workspace,
} from '@core/models'
import {
    FieldFacadeService,
    FieldTypeFacadeService,
    SchemaFacadeService,
    WorkspaceFacadeService,
} from '@core/services/store-facade'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Dictionary } from '@ngrx/entity'
import { FieldValidationService } from '@services/field-validation.service'
import { EnvDirective } from '@shared/env.directive'
import { find, isEqual } from 'lodash-es'
import { combineLatest, Observable, of, switchMap } from 'rxjs'
import { map, take } from 'rxjs/operators'
import {
    AddFieldContentComponent,
    FieldConfiguration,
    NEW_SETTINGS_TYPES,
} from './add-field-content/add-field-content.component'

@UntilDestroy()
@Component({
    selector: 'app-add-field',
    templateUrl: './add-field.component.html',
    styleUrls: ['./add-field.component.sass'],
    imports: [
        TranslocoModule,
        ModalLayoutComponent,
        ReactiveFormsModule,
        TbSelectComponent,
        TbOptionComponent,
        TbInputComponent,
        AddFieldContentComponent,
        TbToggleComponent,
        CellContainerComponent,
        TbButtonComponent,
        ShowIfOperationCodeMatchExpectedOperationDirective,
        AsyncPipe,
        InputCellContainerComponent,
        TbMenuListComponent,
        TbMenuListItemComponent,
        EnvDirective,
        NgTemplateOutlet,
        TbButtonWithOptionsComponent,
        ModalLayoutButtonsComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddFieldComponent
    extends ModalContainerComponent<AddFieldData, Field | undefined>
    implements OnInit
{
    formGroup!: FormGroup<{
        columnName: FormControl<string | null>
        fieldType: FormControl<string | null>
        workspace?: FormControl<string | null>
        onTop: FormControl<boolean | null>
        required: FormControl<boolean | null>
        defaultValue: FormControl<any>
        sharedWorkspaces: FormControl<string[] | null>
    }>
    field!: Field
    editMode = true
    isColumnContentValid = true
    isRequired: boolean = false
    workspacesForMove!: Workspace[]
    workspacesForShare!: Workspace[]
    shareAvailable = false
    supportNewContainer!: boolean
    supportNewSettings = false
    globalWorkspaceGuid!: string
    currentWorkspaceValue!: string
    fieldConfiguration!: FieldConfiguration | null
    selectedSchemaGuid!: string
    protected readonly FieldTypes = FieldTypes
    private readonly manageFieldsService = inject(ManageFieldsService)
    private readonly fieldTypeFacadeService = inject(FieldTypeFacadeService)
    availableFieldTypes$: Observable<FieldType[]> = this.getFieldTypes().pipe(
        switchMap((fieldTypes) => this.updateAvailableFieldTypes(fieldTypes)),
    )
    allFieldTypesDictionary$: Observable<Dictionary<FieldType>> =
        this.fieldTypeFacadeService.selectFieldTypeEntities$
    private readonly schemaFacadeService = inject(SchemaFacadeService)
    private readonly fieldFacadeService = inject(FieldFacadeService)
    private readonly workspaceFacadeService = inject(WorkspaceFacadeService)
    workspaces$: Observable<Workspace[]> = this.workspaceFacadeService.selectAllWorkspaces$
    private readonly templateConfigurationService = inject(WorkspaceTemplateConfigurationService)
    private readonly translation = inject(TranslocoService)
    private readonly fieldValidationService = inject(FieldValidationService)
    private readonly requiredTypes: string[] = [FieldTypes.STATUS, FieldTypes.WATCH]

    get isContentValid() {
        return this.formGroup.invalid || !this.isColumnContentValid
    }

    ngOnInit() {
        if (!this.data.guid || this.data.duplicate) {
            this.editMode = false
        }

        this.supportNewContainer = this.hasNewContainerSupport(this.data.field_type_code)
        this.supportNewSettings = this.hasNewSettingsSupport(this.data.field_type_code)

        combineLatest([
            this.workspaceFacadeService.selectWorkspacesForMove$(
                this.data.field_type_code,
                this.data.operation_code_entity,
                this.data.workspace_guid,
            ),
            this.workspaceFacadeService.selectSelectedWorkspaceGuid$,
            this.workspaceFacadeService.selectGlobalWorkspace$,
            this.schemaFacadeService.selectSelectedTableSchema$,
            this.templateConfigurationService.getTemplateSystemSchemaFields$(),
            this.translation.selectTranslate('application.field_name'),
        ])
            .pipe(untilDestroyed(this))
            .subscribe(
                ([
                    workspaces,
                    selectedWorkspaceGuid,
                    globalWorkspace,
                    schema,
                    templateSchemaFields,
                    fieldName,
                ]) => {
                    if (!schema || !templateSchemaFields) return
                    this.selectedSchemaGuid = schema.guid

                    const templateFields = this.data.new_workspace
                        ? this.data.existing_fields!
                        : templateSchemaFields
                    if (!this.editMode) this.data = this.updateFieldByType(templateFields)

                    this.workspacesForMove = workspaces
                    this.globalWorkspaceGuid = globalWorkspace.guid
                    this.workspacesForShare = this.workspacesForMove.filter(
                        (workspace) =>
                            !isWorkspaceGlobal(workspace) &&
                            workspace.guid !== this.data.workspace_guid,
                    )

                    this.isRequired = !!this.data.is_required
                    this.shareAvailable = this.isShareAvailable(
                        this.data.workspace_guid ?? selectedWorkspaceGuid,
                    )

                    this.initForm(selectedWorkspaceGuid, fieldName, templateFields)
                    this.setDefaultState()
                },
            )
    }

    valid() {
        return this.formGroup.invalid
    }

    saveAndDuplicate() {
        const duplicateData = {
            ...this.field,
            name: `${this.field.name} copy`,
            duplicate: true,
        }

        this.saveWithAddFieldOpen(duplicateData)
    }

    saveAndCreateAnotherOne() {
        const createData = {
            field_type_code: this.data.field_type_code,
            workspace_guid: this.data.workspace_guid,
            new_workspace: this.data.new_workspace,
        }

        this.saveWithAddFieldOpen(createData)
    }

    saveColumn() {
        if (this.isContentValid) {
            this.setIncorrectInputError()
            return
        }

        if (this.fieldConfiguration) {
            this.field.configure_json = JSON.stringify(this.fieldConfiguration)
        }

        if (this.data.new_workspace) {
            this.close(this.field)
            return
        }

        this.wrapResponse(this.getUpdateObservable())
    }

    configurationChanged(configureValue: FieldConfiguration) {
        this.fieldConfiguration = configureValue
    }

    changedValue(field: Field) {
        // TODO: will be deleted during cell type rework or following issues
        // https://chat.anveo.com/git/AnveoTikibase_ui/issues/566
        if (this.field.default_value !== field.default_value) {
            this.defaultValueChanged(field.default_value ?? '')
        }
        if (!field.guid) {
            this.field = {
                ...this.field,
                select_object_field: field.select_object_field,
                configure_json: field.configure_json,
                link_definition: field.link_definition,
            }
            return
        }
        this.field = field
    }

    setDefaultState() {
        // TODO: will be deleted during cell type rework or following issues
        // https://chat.anveo.com/git/AnveoTikibase_ui/issues/566
        this.field = {
            ...this.data,
            field_type_code: this.formGroup.controls.fieldType.value || this.data.field_type_code,
            guid: this.data.guid ? this.data.guid : '',
            schema_guid: this.selectedSchemaGuid,
            select_object_field: this.data.select_object_field,
            configure_json: this.data.configure_json,
            link_definition: this.data.link_definition,
        }
    }

    delete() {
        this.wrapResponse(this.manageFieldsService.deleteField(this.field.guid))
    }

    defaultValueChanged(value: string) {
        this.formGroup.controls.defaultValue.setValue(value)
    }

    hasNewContainerSupport(type: string) {
        return NEW_CONTAINER_CELLS.includes(type)
    }

    hasNewSettingsSupport(type: string) {
        return NEW_SETTINGS_TYPES.includes(type)
    }

    private initForm(
        selectedWorkspaceGuid: string,
        fieldName: string,
        templateSchemaFields: FieldEntities,
    ) {
        this.formGroup = new FormGroup({
            columnName: new FormControl(this.data.name ? this.data.name : '', {
                validators: [Validators.required, Validators.pattern(whitespaceValidationPattern)],
                asyncValidators: this.fieldValidationService.maxLengthValidator('80', fieldName),
            }),
            fieldType: new FormControl<string>(this.data.field_type_code, Validators.required),
            onTop: new FormControl<boolean>(!!this.data.is_on_top),
            required: new FormControl<boolean>(
                !!this.data.is_required || this.requiredTypes.includes(this.data.field_type_code),
            ),
            defaultValue: new FormControl(this.data.default_value),
            sharedWorkspaces: new FormControl<string[]>(this.data.shared_with_workspaces ?? []),
        })
        if (!this.data.new_workspace) {
            this.formGroup.controls.workspace = new FormControl({
                value: this.data.workspace_guid ?? selectedWorkspaceGuid,
                disabled: !this.isMoveAvailable(),
            })
        }

        this.updateRequiredValidator(this.formGroup.controls.defaultValue)

        if (this.editMode) {
            this.formGroup.controls.fieldType.disable()
            this.formGroup.updateValueAndValidity()
        }

        this.currentWorkspaceValue = this.formGroup.controls.workspace?.value ?? ''

        this.updateRequiredControl(this.formGroup.controls.required, this.data.field_type_code)

        this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe((changes) => {
            if (changes.required !== undefined) {
                this.isRequired = !!changes.required
                if (!this.isRequired && this.formGroup.controls.defaultValue.value) {
                    this.formGroup.controls.defaultValue.setValue('', { emitEvent: false })
                }
            }

            if (!this.data.new_workspace)
                this.workspaceUpdate(selectedWorkspaceGuid, changes.workspace)

            const shared_with_workspaces = this.sharedUpdate(changes.sharedWorkspaces) ?? []

            if (!isEqual(shared_with_workspaces, changes.sharedWorkspaces)) {
                this.formGroup.controls.sharedWorkspaces.setValue(shared_with_workspaces, {
                    emitEvent: false,
                })
            }

            this.field = {
                ...this.field,
                workspace_guid: !this.data.new_workspace ? this.currentWorkspaceValue : '',
                is_required: this.isRequired ? 1 : 0,
                name: changes.columnName!,
                is_on_top: changes.onTop ? 1 : 0,
                default_value: changes.defaultValue,
                shared_with_workspaces,
            }

            this.updateRequiredValidator(this.formGroup.controls.defaultValue)

            if (changes.fieldType && changes.fieldType !== this.field.field_type_code) {
                this.field.field_type_code = changes.fieldType
                this.supportNewContainer = this.hasNewContainerSupport(changes.fieldType)
                this.supportNewSettings = this.hasNewSettingsSupport(changes.fieldType)
                this.fieldConfiguration = null

                if (!this.editMode && this.requiredTypes.includes(changes.fieldType)) {
                    const templateField = find(
                        templateSchemaFields,
                        (field) => field.field_type_code === this.data.field_type_code,
                    )

                    if (templateField) {
                        this.field = {
                            ...this.field,
                            ...templateField,
                        }
                    }
                }
            }

            this.updateRequiredControl(this.formGroup.controls.required, this.field.field_type_code)
        })
    }

    private workspaceUpdate(selectedWorkspaceGuid: string, workspaceGuid?: string | null) {
        const currentWorkspace =
            workspaceGuid ?? this.formGroup.controls.workspace?.value ?? selectedWorkspaceGuid

        this.workspacesForShare = this.workspacesForMove.filter(
            (workspace) => !isWorkspaceGlobal(workspace) && workspace.guid !== currentWorkspace,
        )

        if (
            currentWorkspace === this.globalWorkspaceGuid &&
            this.currentWorkspaceValue !== this.globalWorkspaceGuid
        ) {
            this.callConfirmationPopup()
            return
        }

        this.currentWorkspaceValue = currentWorkspace
        this.shareAvailable = this.isShareAvailable(currentWorkspace)
    }

    private sharedUpdate(sharedValue?: string[] | null) {
        if (!sharedValue?.includes(this.currentWorkspaceValue)) return sharedValue

        return sharedValue?.filter((workspace) => workspace !== this.currentWorkspaceValue)
    }

    private callConfirmationPopup() {
        this.formGroup.controls.workspace?.setValue(this.currentWorkspaceValue, {
            emitEvent: false,
        })
        this.confirmationDialogService
            .openCommon({
                translations: {
                    title: 'manage_fields.set_global_workspace_title',
                    message: 'manage_fields.set_global_workspace_message',
                    cancel: 'manage_fields.set_global_workspace_save',
                    confirm: 'manage_fields.set_global_workspace_cancel',
                },
            })
            .subscribe((result) => {
                if (result === CommonDialogResultStatus.CANCEL) {
                    this.currentWorkspaceValue = this.globalWorkspaceGuid
                    this.shareAvailable = false
                    this.formGroup.controls.sharedWorkspaces.setValue([], { emitEvent: false })
                    this.formGroup.controls.workspace?.setValue(this.currentWorkspaceValue)
                }
            })
    }

    private saveWithAddFieldOpen(data: AddFieldData) {
        if (this.isContentValid) {
            this.setIncorrectInputError()
            return
        }

        if (this.data.new_workspace) {
            this.close(this.field)
            return
        }

        this.wrapResponseObservable(this.getUpdateObservable()).subscribe((updateData) => {
            this.openAddField(data, updateData)
        })
    }

    private openAddField(updateData: AddFieldData, data: UpdateResponseModel) {
        if (data.status === 'success') {
            this.close()
            this.modalManagerService
                .openDialog<AddFieldComponent, AddFieldData | null>({
                    component: AddFieldComponent,
                    data: updateData,
                })
                .pipe(take(1))
        }
    }

    private isMoveAvailable() {
        return this.shareAvailable && !this.data.operation_code_entity?.freeze
    }

    private isShareAvailable(guid: string) {
        if (this.data.new_workspace) return !!this.workspacesForShare.length

        return (
            !this.data.operation_code_entity?.global &&
            !isWorkspaceGlobal(this.getWorkspaceByGuid(guid)) &&
            !!this.workspacesForShare.length
        )
    }

    private getWorkspaceByGuid(workspaceGuid: string) {
        return this.workspacesForMove.find((workspace) => workspace.guid === workspaceGuid)!
    }

    private getUpdateObservable() {
        return this.fieldTypeFacadeService.selectFieldTypeEntities$.pipe(
            take(1),
            switchMap((fieldTypes) => {
                if (this.editMode) {
                    return this.fieldFacadeService.updateField(this.field, fieldTypes)
                }

                return this.fieldFacadeService.createField(this.field)
            }),
        )
    }

    // TODO: will be updated in issue #2196
    private updateFieldByType(templateSchemaFields: FieldEntities) {
        if (!this.data.one_per_workspace) {
            return this.data
        }

        const templateField = find(
            templateSchemaFields,
            (field) => field.field_type_code === this.data.field_type_code,
        )

        return {
            ...templateField,
            ...this.data,
        }
    }

    private setIncorrectInputError() {
        this.errors = [this.translation.translate('dialog_errors.incorrect_input')]
    }

    private wrapResponse(observable: Observable<UpdateResponseModel>) {
        this.wrapResponseObservable<UpdateResponseModel>(observable).subscribe((data) => {
            if (data.status === 'success') {
                this.close()
            }
        })
    }

    private updateRequiredControl(requiredControl: FormControl<any>, code: string) {
        const disabledForCreateTypes: string[] = [
            FieldTypes.STATUS,
            FieldTypes.ASSIGNEE,
            FieldTypes.DESCRIPTION,
        ]
        const isStatus = code === FieldTypes.STATUS

        if (
            requiredControl.enabled &&
            this.data.new_workspace &&
            disabledForCreateTypes.includes(code)
        ) {
            requiredControl.disable()
            requiredControl.setValue(isStatus, { emitEvent: false })
            this.isRequired = isStatus
            return
        }

        const isRequired = this.requiredTypes.includes(code)

        if (!isRequired && requiredControl.disabled) {
            requiredControl.enable()
            requiredControl.setValue(isStatus)
            this.isRequired = isStatus
        }

        if (isRequired && requiredControl.enabled) {
            requiredControl.disable()
            requiredControl.setValue(isStatus)
            this.isRequired = isStatus
        }
    }

    private updateRequiredValidator(defaultValueControl: FormControl<any>) {
        if (this.isRequired && !defaultValueControl.hasValidator(Validators.required)) {
            defaultValueControl.addValidators(Validators.required)
            defaultValueControl.updateValueAndValidity()
        }

        if (!this.isRequired && defaultValueControl.hasValidator(Validators.required)) {
            defaultValueControl.removeValidators(Validators.required)
            defaultValueControl.updateValueAndValidity()
        }
    }

    private getFieldTypes() {
        if (!this.data.new_workspace)
            return this.fieldTypeFacadeService.selectAvailableFieldTypeByWorkspace$

        return this.fieldTypeFacadeService.selectAvailableFieldTypesForNewWorkspace$(
            this.data.existing_fields!,
        )
    }

    private updateAvailableFieldTypes(fieldTypes: FieldType[]) {
        if (!this.editMode) return of(fieldTypes)

        return this.allFieldTypesDictionary$.pipe(
            map((fieldTypesDict) => {
                if (fieldTypes.find((type) => type.guid === this.data.field_type_code)) {
                    return fieldTypes
                }

                const currentType = fieldTypesDict[this.data.field_type_code]

                if (!currentType) return fieldTypes

                return [...fieldTypes, currentType]
            }),
        )
    }
}
